import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, FileText, BarChart3, Presentation, Users, Cloud, Share2, Calendar, Bot, Newspaper, Clock, ArrowRight, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const Office365Page = ({ setCurrentSection, onSelectGuide, guides }) => {
  const navigate = useNavigate();
  // Function to count procedures in a guide by analyzing internal links
  const countProceduresInGuide = (guideId) => {
    const guide = guides.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        // Count links in format [text](link-id)
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    
    return linkCount;
  };
  // Organiser les guides par outils
  const toolCategories = {
    outlook: {
      name: 'Outlook',
      icon: Mail,
      description: 'Messagerie et calendrier professionnel',
      color: 'from-blue-500 to-indigo-600',
      guideId: 'outlook-basics'
    },
    word: {
      name: 'Word',
      icon: FileText,
      description: 'Traitement de texte et collaboration',
      color: 'from-blue-600 to-blue-700',
      guideId: 'word-document-mastery'
    },
    excel: {
      name: 'Excel',
      icon: BarChart3,
      description: 'Tableurs et analyses de données',
      color: 'from-green-500 to-green-600',
      guideId: 'excel-data-mastery'
    },
    powerpoint: {
      name: 'PowerPoint',
      icon: Presentation,
      description: 'Présentations dynamiques et professionnelles',
      color: 'from-red-500 to-red-600',
      guideId: 'powerpoint-presentation-mastery'
    },
    teams: {
      name: 'Teams',
      icon: Users,
      description: 'Collaboration et communication d\'équipe',
      color: 'from-purple-500 to-purple-600',
      guideId: 'teams-collaboration'
    },
    onedrive: {
      name: 'OneDrive',
      icon: Cloud,
      description: 'Stockage cloud et synchronisation',
      color: 'from-cyan-500 to-cyan-600',
      guideId: 'onedrive-storage'
    },
    sharepoint: {
      name: 'SharePoint',
      icon: Share2,
      description: 'Partage de documents et intranet',
      color: 'from-orange-500 to-orange-600',
      guideId: 'sharepoint-collaboration'
    },
    planner: {
      name: 'Planner',
      icon: Calendar,
      description: 'Gestion de projets et tâches',
      color: 'from-teal-500 to-teal-600',
      guideId: 'planner-basics'
    },
    copilot: {
      name: 'Copilot',
      icon: Bot,
      description: 'Intelligence artificielle intégrée à Office 365',
      color: 'from-indigo-500 to-purple-600',
      guideId: 'copilot-productivity'
    }
  };

  // Articles d'actualités avec contenus complets
  const newsArticles = [
    {
      id: 'outlook-vs-exchange',
      title: 'Quelle différence entre Outlook et Exchange ?',
      description: 'Comprendre la distinction entre le client de messagerie et le serveur de messagerie Microsoft',
      date: '2024-09-20',
      readTime: '5 min',
      category: 'Fondamentaux',
      isNew: true,
      content: {
        introduction: 'Microsoft Outlook et Exchange Server sont deux composants distincts mais complémentaires de l\'écosystème de messagerie Microsoft. Cette confusion est fréquente, clarifions les rôles de chacun.',
        sections: [
          {
            title: '1. Microsoft Outlook : Le client de messagerie',
            content: '• **Définition** : Application installée sur votre ordinateur ou appareil mobile\n• **Fonction principale** : Interface utilisateur pour consulter, rédiger et organiser vos emails\n• **Versions disponibles** : Outlook Desktop, Outlook Web App, Outlook Mobile\n• **Utilisateurs** : Toute personne ayant besoin d\'accéder à ses emails',
            sectionColor: 'bg-blue-50 border-l-4 border-l-blue-500',
            sectionIcon: 'Mail',
            iconColor: 'text-blue-600'
          },
          {
            title: '2. Microsoft Exchange Server : Le serveur de messagerie',
            content: '• **Définition** : Serveur qui stocke et gère les boîtes aux lettres électroniques\n• **Fonction principale** : Infrastructure backend qui traite, stocke et distribue les emails\n• **Versions disponibles** : Exchange Server (on-premise), Exchange Online (cloud)\n• **Gestion** : Administrés par les équipes IT des entreprises',
            sectionColor: 'bg-green-50 border-l-4 border-l-green-500',
            sectionIcon: 'Server',
            iconColor: 'text-green-600'
          },
          {
            title: '3. Comment ils travaillent ensemble',
            content: '• **Connexion** : Outlook se connecte à Exchange pour récupérer les emails\n• **Synchronisation** : Exchange synchronise les emails sur tous vos appareils\n• **Sécurité** : Exchange gère la sécurité, Outlook affiche le contenu filtré\n• **Calendrier** : Exchange stocke les rendez-vous, Outlook les affiche',
            sectionColor: 'bg-purple-50 border-l-4 border-l-purple-500',
            sectionIcon: 'ArrowRightLeft',
            iconColor: 'text-purple-600'
          }
        ],
        conclusion: 'En résumé : Exchange est le "moteur" qui gère vos emails, Outlook est le "tableau de bord" qui vous permet de les consulter et les gérer.'
      }
    },
    {
      id: 'teams-nouvelles-fonctionnalites',
      title: 'Microsoft Teams : Les dernières fonctionnalités',
      description: 'Découvrez les fonctionnalités avancées de Microsoft Teams pour optimiser votre collaboration',
      date: '2024-09-15',
      readTime: '9 min',
      category: 'Actualités',
      isNew: true,
      content: {
        introduction: 'Microsoft Teams s\'est imposé comme la plateforme de collaboration incontournable. Découvrez comment exploiter ses dernières fonctionnalités pour transformer votre façon de travailler en équipe et maximiser votre productivité.',
        sections: [
          {
            title: '1. Intelligence artificielle intégrée avec Copilot',
            content: '• **Résumés automatiques de réunions** : Copilot génère des synthèses détaillées avec points clés et actions à retenir\n• **Transcription intelligente** : Reconnaissance vocale multilingue avec identification des intervenants\n• **Suggestions d\'actions** : Propositions automatiques de tâches basées sur les discussions\n• **Analyse des sentiments** : Détection du ton et de l\'engagement des participants\n• **Préparation de réunions** : Génération automatique d\'agendas basés sur l\'historique',
            sectionColor: 'bg-blue-50 border-l-4 border-l-blue-500',
            sectionIcon: 'Bot',
            iconColor: 'text-blue-600'
          },
          {
            title: '2. Fonctionnalités avancées de réunion',
            content: '• **Modes immersifs personnalisés** : Environnements virtuels professionnels (salle de conférence, café, espace créatif)\n• **Réactions et interactions** : Palette étendue d\'emojis, applaudissements, levée de main virtuelle\n• **Enregistrement cloud automatique** : Stockage sécurisé avec transcription et indexation\n• **Sondages et quiz en temps réel** : Engagement des participants avec résultats instantanés\n• **Arrière-plans dynamiques** : Effets visuels professionnels et filtres adaptatifs\n• **Partage d\'écran amélioré** : Annotation collaborative et pointeur laser',
            sectionColor: 'bg-green-50 border-l-4 border-l-green-500',
            sectionIcon: 'Video',
            iconColor: 'text-green-600'
          },
          {
            title: '3. Collaboration et productivité renforcées',
            content: '• **Co-édition temps réel** : Modification simultanée de documents Office directement dans Teams\n• **Tableaux blancs intelligents** : Templates prêts à l\'emploi, outils de dessin avancés, export automatique\n• **Intégration Power Platform** : Power Apps, Power Automate et Power BI intégrés nativement\n• **Workflows automatisés** : Automatisation des processus métier sans code\n• **Espaces d\'équipe personnalisés** : Organisation sur mesure avec onglets et applications\n• **Notifications intelligentes** : Filtrage adaptatif selon l\'importance et le contexte',
            sectionColor: 'bg-purple-50 border-l-4 border-l-purple-500',
            sectionIcon: 'Users',
            iconColor: 'text-purple-600'
          },
          {
            title: '4. Sécurité et gouvernance avancées',
            content: '• **Chiffrement de bout en bout** : Protection renforcée pour les communications sensibles\n• **Politiques de conformité** : Respect automatique des réglementations sectorielles\n• **Contrôle d\'accès granulaire** : Permissions détaillées par utilisateur et contenu\n• **Audit et traçabilité** : Logs complets des activités pour la conformité\n• **Protection contre le phishing** : Détection automatique des liens et fichiers suspects\n• **Sauvegarde et récupération** : Protection des données avec restauration ponctuelle',
            sectionColor: 'bg-red-50 border-l-4 border-l-red-500',
            sectionIcon: 'CheckCircle',
            iconColor: 'text-red-600'
          }
        ],
        conclusion: 'Microsoft Teams évolue constamment pour offrir une expérience de collaboration inégalée. Ces fonctionnalités avancées positionnent Teams comme le hub central de productivité moderne, intégrant IA, sécurité et collaboration dans un écosystème unifié.'
      }
    },
    {
      id: 'copilot-integration',
      title: 'Microsoft 365 Copilot : Revolution de la productivité',
      description: 'Comment l\'IA transforme votre expérience Office 365 au quotidien',
      date: '2024-09-10',
      readTime: '8 min',
      category: 'Innovation',
      isNew: false,
      content: {
        introduction: 'Microsoft 365 Copilot représente la plus grande révolution de productivité depuis l\'invention du PC. Cette IA intégrée transforme radicalement notre façon de travailler avec les outils Office.',
        sections: [
          {
            title: '1. Copilot dans Word : Rédaction assistée',
            content: '• **Génération de contenu** : Création automatique de documents entiers\n• **Reformulation** : Amélioration du style et de la clarté\n• **Résumés automatiques** : Synthèse de longs documents\n• **Mise en forme intelligente** : Application automatique des styles',
            sectionColor: 'bg-blue-50 border-l-4 border-l-blue-500',
            sectionIcon: 'FileText',
            iconColor: 'text-blue-600'
          },
          {
            title: '2. Copilot dans Excel : Analyse de données révolutionnaire',
            content: '• **Formules automatiques** : Génération de formules complexes en langage naturel\n• **Graphiques intelligents** : Création automatique de visualisations pertinentes\n• **Analyse prédictive** : Prévisions basées sur vos données historiques\n• **Nettoyage de données** : Détection et correction automatique des erreurs',
            sectionColor: 'bg-green-50 border-l-4 border-l-green-500',
            sectionIcon: 'BarChart',
            iconColor: 'text-green-600'
          },
          {
            title: '3. Copilot dans PowerPoint : Présentations automatiques',
            content: '• **Création de slides** : Génération automatique de présentations complètes\n• **Design intelligent** : Application automatique de thèmes cohérents\n• **Suggestions de contenu** : Propositions d\'améliorations et d\'ajouts\n• **Adaptation audience** : Personnalisation selon le public cible',
            sectionColor: 'bg-orange-50 border-l-4 border-l-orange-500',
            sectionIcon: 'Presentation',
            iconColor: 'text-orange-600'
          },
          {
            title: '4. Impact sur la productivité',
            content: '• **Gain de temps** : Réduction de 70% du temps de création de contenu\n• **Qualité améliorée** : Contenu plus professionnel et cohérent\n• **Créativité débridée** : Plus de temps pour la réflexion stratégique\n• **Apprentissage continu** : L\'IA s\'adapte à votre style de travail',
            sectionColor: 'bg-purple-50 border-l-4 border-l-purple-500',
            sectionIcon: 'TrendingUp',
            iconColor: 'text-purple-600'
          }
        ],
        conclusion: 'Microsoft 365 Copilot n\'est pas juste un outil, c\'est un assistant intelligent qui révolutionne notre approche du travail. L\'avenir de la productivité est là.'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Full Width Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 p-4 rounded-2xl mr-6 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
              <Mail className="h-16 w-16 text-white" />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">Office 365</h1>
              <p className="text-blue-100 text-xl md:text-2xl max-w-4xl leading-relaxed">
                Maîtrisez la suite Microsoft Office 365 : Outlook, Teams, OneDrive et tous les outils essentiels
              </p>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-cyan-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[{ id: 'office365', label: 'Office 365' }]}
          onNavigate={setCurrentSection}
        />

        {/* Section Nouveau sur Office 365 */}
        <section className="mb-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-8 shadow-lg">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Bot className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-6">
              Nouveau sur Office 365 ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Découvrez les différents outils de la suite Microsoft et leurs fonctionnalités principales
            </p>
            <Button 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300"
              onClick={() => navigate('/office365/presentation')}
            >
              <Presentation className="h-6 w-6 mr-3" />
              Découvrir Office 365 en slides
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
          </div>
        </section>

        {/* Section Guides par Outil */}
        <section className="mb-16 bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-8 shadow-lg">
          <div className="flex items-center mb-8">
            <Star className="h-8 w-8 text-purple-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Guides par Outil</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(toolCategories).map(([key, tool]) => {
              const IconComponent = tool.icon;
              const toolGuide = guides.find(guide => guide.id === tool.guideId);
              const hasGuides = toolGuide ? true : false;
              const procedureCount = hasGuides ? countProceduresInGuide(tool.guideId) : 0;
              
              return (
                <Card 
                  key={key} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border-0 overflow-hidden bg-white h-full flex flex-col"
                  onClick={() => hasGuides && onSelectGuide({id: tool.guideId, title: `${tool.name} Guide`})}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {tool.name}
                    </CardTitle>
                    <div className="flex items-center justify-center text-gray-600 text-sm mb-3">
                      {hasGuides ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                          {procedureCount > 0 ? `${procedureCount} procédure${procedureCount > 1 ? 's' : ''}` : '1 guide'}
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4 mr-1 text-orange-500" />
                          Bientôt disponible
                        </>
                      )}
                    </div>
                    <CardDescription className="text-gray-600 mb-6 leading-relaxed">
                      {tool.description}
                    </CardDescription>
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${hasGuides ? `group-hover:bg-gradient-to-r group-hover:${tool.color}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!hasGuides}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        hasGuides 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {hasGuides ? 'Consulter le guide' : 'Prochainement'}
                      {hasGuides && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {hasGuides && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Office365Page;